import axios from 'axios';
import LocalStorageService from 'services/local-storage-service';

import type {
  AutocompleteRequest,
  AutocompleteResponse,
  PlaceDetailResponse,
  LocationData,
} from './interfaces';
import type { PartnerId } from 'shared/domain';

class LocationService {
  private getBaseUrl(): string {
    return LocalStorageService.getBaseUrl() ?? '';
  }

  private getPartnerId(): PartnerId {
    return parseInt(LocalStorageService.getPartnerId() ?? '');
  }

  /**
   * Get address autocomplete suggestions
   * @param text - Text being typed by the user
   * @param filterCountries - Array of country codes to filter (e.g., ["MEX"])
   * @param maxResults - Maximum number of results
   * @returns Promise with address suggestions
   */
  async getPlacesAutocomplete(
    text: string,
    filterCountries: string[] = [],
    maxResults: number = 5,
  ): Promise<AutocompleteResponse> {
    const baseUrl = this.getBaseUrl();
    const partnerId = this.getPartnerId();

    if (!baseUrl || !partnerId) {
      throw new Error('Base URL or Partner ID not found in localStorage');
    }

    const requestData: AutocompleteRequest = {
      text,
      filterCountries,
      maxResults,
    };

    const response = await axios.post(
      `${baseUrl}api/rs-aliados-gateway/location-services/places/${partnerId}/autocomplete`,
      requestData,
    );

    return response.data;
  }

  /**
   * Get complete details of a specific place
   * @param placeId - Unique ID of the place obtained from autocomplete
   * @param language - Language code (default 'es')
   * @returns Promise with place details including coordinates
   */
  async getPlaceDetail(
    placeId: string,
    language: string = 'es', // TODO: need dynamic to use 'es' or 'pt' depend language of platform web
  ): Promise<PlaceDetailResponse> {
    const baseUrl = this.getBaseUrl();
    const partnerId = this.getPartnerId();

    if (!baseUrl || !partnerId) {
      throw new Error('Base URL or Partner ID not found in localStorage');
    }

    const response = await axios.get(
      `${baseUrl}api/rs-aliados-gateway/location-services/places/${partnerId}/${placeId}/${language}`,
    );

    return response.data;
  }

  /**
   * Convert the service response to the format expected by the existing component
   * @param placeDetailResponse - Response from the place details service
   * @param originalText - Original text selected by the user
   * @returns Object in a format compatible with the existing component
   */
  formatLocationData(
    placeDetailResponse: PlaceDetailResponse,
    originalText: string,
  ): LocationData {
    const { Place } = placeDetailResponse;
    const [lng, lat] = Place.Geometry.Point; // AWS returns [longitude, latitude]

    return {
      completeAddress: Place.Label,
      parseAddress: originalText, // Keep the original selected text
      coordinates: { lat, lng },
      city: Place.Municipality,
    };
  }

  /**
   * Convenience method that combines autocomplete and place details
   * @param placeId - Place ID
   * @param originalText - Original selected text
   * @param language - Language code
   * @returns Formatted LocationData
   */
  async getLocationDataByPlaceId(
    placeId: string,
    originalText: string,
    language: string = 'es', // TODO: need dynamic to use 'es' or 'pt' depend language of platform web
  ): Promise<LocationData> {
    const placeDetail = await this.getPlaceDetail(placeId, language);
    return this.formatLocationData(placeDetail, originalText);
  }
}

export default new LocationService();
